/**
 * The **Monstrosity Structure Damage** table (Core p.324; the v3 NPC pack's `tables.json`,
 * `core-monstrosities`): what a Monstrosity with Structure (an Elite or Ultra) rolls instead of the
 * mech Structure Damage table. Unique Physiology names it; `spawn/structureTableFreeze.ts` freezes
 * it onto `base.structureTable`, and {@link import("./tables").resolveStructureDamage} hands the
 * check here when that table is this one.
 *
 * The roll is the mech table's: 1d6 per structure damage marked, including the point just taken,
 * lowest face read. The pack's bands decide which row the roll lands on; this module decides what
 * each row DOES, keyed on the band's `min` (the pack titles two bands "Dismemberment"):
 *
 *   - **5-6 Glancing Hit** — IMPAIRED until the end of its next turn.
 *   - **3-4 Powerful Hit** — knocked PRONE.
 *   - **2 Dismemberment** — 1d6 Kinetic damage, and SLOWED for the rest of the scene.
 *   - **1 Dismemberment** — by remaining Structure: 3+ STUNNED until the end of its next turn;
 *       2 a HULL save or destroyed; 1 destroyed.
 *   - **multiple 1s (`-1`) Fatal Hit** — destroyed.
 *
 * A band this module has no outcome for (a pack that re-cut the table) lands its witness and
 * a GM-adjudicates note carrying the row's text, never a guessed effect.
 */
import type { Combatant, DamageStep, FrozenStructureTable, ResolvedStep, StatusExpiry } from "@lancer-vtt/domain";
import { saveBonusFor } from "@lancer-vtt/domain";
import type { DiceSource } from "../dice";
import { mitigateDamageInstance } from "./resolve/damagePacket";
import { statusBlockedByImmunity } from "./effects/seed";
import { nonAttackArmor, nonAttackExposed, nonAttackImmune, nonAttackImmuneWarning, nonAttackResistant } from "./effects/nonAttackResistance";
import { rollSelfCheck, type SelfCheckContext } from "./selfCheck";
import type { TableResult } from "./tables";
import { MONSTROSITY_STRUCTURE_TABLE } from "./spawn/structureTableFreeze";

export { MONSTROSITY_STRUCTURE_TABLE };

/** The band `min` the pack uses for the "multiple 1s" row. */
const MULTIPLE_ONES_BAND = -1;

export type ResolveMonstrosityStructureArgs = {
  combatant: Combatant;
  table: FrozenStructureTable;
  /** Structure damage marked, including the point just taken. */
  marked: number;
  /** `current.structure` after the decrement. */
  remainingStructure: number;
  dice: DiceSource;
  /** Stamped onto the next-turn IMPAIRED and STUNNED, as the mech table's are. */
  turnOrdinal: number;
  /** Folds the HULL save's modifiers and gates status immunity, damage mitigation; absent in direct fixtures. */
  selfCheck?: SelfCheckContext;
};

export function resolveMonstrosityStructure(args: ResolveMonstrosityStructureArgs): TableResult {
  const { combatant, table, marked, remainingStructure, dice, turnOrdinal, selfCheck } = args;
  const faces: number[] = [];
  for (let i = 0; i < Math.max(1, marked); i++) faces.push(dice.roll(6));
  const lowest = Math.min(...faces);
  const ones = faces.filter((f) => f === 1).length;
  const band = ones >= 2 ? table.results.find((r) => r.min === MULTIPLE_ONES_BAND) : table.results.find((r) => r.min <= lowest && lowest <= r.max && r.min > 0);

  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  let destroyed = false;
  let excessDamage = 0;
  const nextTurn: StatusExpiry = { kind: "turn", edge: "end", who: "target" };
  const addStatus = (status: string, expiry: StatusExpiry, applied?: number): void => {
    if (selfCheck !== undefined && statusBlockedByImmunity({ state: selfCheck.state, targetId: combatant.id, name: status, ...(selfCheck.overlay !== undefined ? { overlay: selfCheck.overlay } : {}) })) {
      warnings.push(`${band?.title}: ${combatant.name} is immune to ${status}.`);
      return;
    }
    steps.push({ op: "status_add", target: combatant.id, status, expiry, ...(applied !== undefined ? { appliedTurn: applied } : {}) });
  };
  const destroy = (): void => {
    steps.push({ op: "destroy", target: combatant.id, destroyed: true });
    destroyed = true;
  };

  switch (band?.min) {
    case 5:
      addStatus("Impaired", nextTurn, turnOrdinal);
      break;
    case 3:
      addStatus("Prone", { kind: "until_removed" });
      break;
    case 2: {
      const face = dice.roll(6);
      const damage = kineticDamage({ combatant, face, source: band!.title, selfCheck });
      steps.push(damage.step);
      warnings.push(...damage.warnings);
      excessDamage = damage.dealt;
      addStatus("Slowed", { kind: "scene" });
      break;
    }
    case 1:
      if (remainingStructure >= 3) {
        addStatus("Stunned", nextTurn, turnOrdinal);
      } else if (remainingStructure === 2) {
        const roll = rollSelfCheck({ ctx: selfCheck, dice, combatant, against: "hull", bonus: saveBonusFor(combatant, "hull"), dc: combatant.base.saveTarget ?? 10, roll: "save" });
        steps.push(...roll.steps);
        warnings.push(...roll.warnings);
        if (roll.save.result === "fail") destroy();
      } else {
        destroy();
      }
      break;
    case MULTIPLE_ONES_BAND:
      destroy();
      break;
    default:
      warnings.push(
        band === undefined
          ? `${table.title}: no row for a roll of ${lowest} on ${combatant.name} — GM adjudicates.`
          : `${table.title} — ${band.title}: not automated, GM adjudicates: ${band.result}`,
      );
  }

  steps.unshift({
    op: "table_check",
    kind: "structure_damage",
    target: combatant.id,
    dice: faces,
    lowest,
    ones,
    track: remainingStructure,
    row: band?.title ?? "No row",
    table: table.title,
  });
  return { steps, warnings, destroyed, ...(excessDamage > 0 ? { excessDamage } : {}) };
}

/** Dismemberment's 1d6 Kinetic: Exposed, Armor, Resistance and Immunity apply as at any non-attack site (no source character). */
function kineticDamage(args: { combatant: Combatant; face: number; source: string; selfCheck: SelfCheckContext | undefined }): { step: DamageStep; dealt: number; warnings: string[] } {
  const { combatant, face, source, selfCheck } = args;
  const state = selfCheck?.state;
  const overlay = selfCheck?.overlay;
  const immune = state !== undefined && nonAttackImmune({ state, targetId: combatant.id, dtype: "kinetic", overlay });
  // The packet's mitigation (`resolve/damagePacket.ts`); the structure table that rolled it lands it.
  const { witness: step } = mitigateDamageInstance({
    target: combatant,
    // Exposed doubles it before any reduction (Core p.77, BC-069).
    instance: { dtype: "kinetic", amount: state !== undefined && nonAttackExposed({ state, targetId: combatant.id, attackerId: null, overlay }) ? face * 2 : face, dice: [face], sides: 6, bonus: 0 },
    ap: false,
    armor: state === undefined ? combatant.base.armor : nonAttackArmor({ state, targetId: combatant.id, attackerId: null, overlay, armor: combatant.base.armor }),
    resistant: state !== undefined && nonAttackResistant({ state, targetId: combatant.id, attackerId: null, dtype: "kinetic", overlay }),
    reduce: 0,
    immune,
    witness: { source: { kind: "effect", name: source } },
  });
  return { step, dealt: immune ? 0 : step.final, warnings: immune ? [nonAttackImmuneWarning(combatant.name, "kinetic")] : [] };
}
